<template>
  <v-dialog
    v-model="dialog"
    width="50%"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="headline"
        >Add Files To "<b>#{{ item.id }}</b
        >"</v-card-title
      >
      <v-card-text>
        <v-container>
          <v-alert type="info" v-if="form.selectedItems.length > 0">
            You are selecting <b>{{ form.selectedItems.length }}</b> files to be
            associated with "<b>#{{ item.id }}</b
            >"
          </v-alert>
          <v-alert v-else type="warning">
            Please select file to be associated with "<b>#{{ item.id }}</b
            >"
          </v-alert>
          <v-row>
            <v-col cols="12" sm="12" md="3" lg="3">
              <v-text-field
                v-model="filter.SearchTerm"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                solo
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="12" md="3" lg="3">
              <TagsSelect
                v-model="filter.tags"
                label="Filter by Tags"
                hide-details
                solo
                clearable
                multiple
                dense
              />
            </v-col>
            <v-col cols="12" sm="12" md="6" lg="6" class="text-right">
              <v-btn
                color="info"
                dark
                class="mb-2"
                @click="onAdd()"
                v-show="hasAccess('storage.add')"
              >
                <v-icon>upload_file</v-icon>&nbsp;&nbsp;New File
              </v-btn>

              <v-btn
                color="info"
                dark
                class="mb-2 ml-1"
                @click="onAddFolder()"
                v-show="hasAccess('storage.add')"
              >
                <v-icon>create_new_folder</v-icon> New Folder
              </v-btn>
            </v-col>
          </v-row>

          <template>
            <v-card :loading="loading">
              <v-card-title>
                <div v-for="(item, i) in folderPath" :key="i">
                  <span v-if="item[0]"><small> / </small></span>
                  <router-link :to="`?folderId=${item[0]}`">
                    <small>{{ item[1] }}</small>
                  </router-link>
                </div>
              </v-card-title>
              <v-skeleton-loader
                v-if="isLoading"
                class="mx-auto"
                type="table"
              ></v-skeleton-loader>
              <v-data-table
                v-else
                :headers="activeHeaders"
                :items="sortedDataItems"
                class="elevation-1"
                :options.sync="tableOptions"
                :server-items-length="totalItems"
                :hide-default-footer="true"
                :footer-props="{
                  'items-per-page-options': [10, 20, 30, 40, 50, 100, 200],
                }"
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <v-menu offset-y :close-on-content-click="false">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          rounded
                          class="content-ma-0 mr-2"
                          v-on="on"
                          x-small
                        >
                          <v-icon small>menu</v-icon> Customize Columns
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          dense
                          :key="i"
                          v-for="(header, i) in headers"
                        >
                          <v-list-item-title v-if="header.readonly">
                            <v-checkbox
                              v-model="headers[i].active"
                              :label="header.text"
                              :disabled="true"
                            />
                          </v-list-item-title>
                          <v-list-item-title v-else>
                            <v-checkbox
                              v-model="headers[i].active"
                              :label="header.text"
                            />
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-toolbar>
                </template>

                <template v-slot:item.lastUpdated="{ item }">
                  <v-chip @click="onEdit(item)" small>
                    {{ item.lastUpdated | formatDatetime }}
                  </v-chip>
                </template>

                <template v-slot:item.dateCreated="{ item }">
                  <v-chip @click="onEdit(item)" small>
                    {{ item.dateCreated | formatDatetime }}
                  </v-chip>
                </template>

                <template v-slot:item.categories="{ item }">
                  <TagsChip :value="item.categories" />
                </template>

                <template v-slot:item.date="{ item }">
                  <v-chip small @click="onEdit(item)">
                    {{ item.date | formatDate }}
                  </v-chip>
                </template>

                <template v-slot:item.userId="{ item }">
                  <span @click="onEdit(item)">
                    <UsersChip :value="item.taskUsers" />
                  </span>
                </template>

                <template v-slot:item.size="{ item }">
                  <div>
                    <span v-if="!item.isFolder">
                      {{ item.size ? bytesToSize(item.size) : "" }}
                    </span>
                    <small v-else> {{ item.totalFiles }} File </small>
                  </div>
                </template>

                <template v-slot:item.status="{ item }">
                  <span @click="onEdit(item)"
                    ><TaskStatusChip :status="item.status"
                  /></span>
                </template>

                <template v-slot:item.name="{ item }">
                  <span
                    @click="openFolder(item)"
                    v-if="item.isFolder"
                    style="cursor: pointer"
                  >
                    <v-icon>{{ item.icon }}</v-icon> {{ item.name }}
                  </span>

                  <span v-else>
                    <v-icon>{{ item.icon }}</v-icon> {{ item.name }}
                  </span>
                </template>

                <template v-slot:item.type="{ item }" @click="onEdit(item)">
                  <span>
                    {{ item.type }}
                  </span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small class="content-ma-0" v-on="on">
                        <v-icon small>mdi-cogs</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        @click="onDownload(item)"
                        v-show="hasAccess('storage.edit') && !item.isFolder"
                      >
                        <v-list-item-title>
                          <v-icon small left>mdi-download</v-icon>Download
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        @click="onEdit(item)"
                        v-show="hasAccess('storage.edit')"
                      >
                        <v-list-item-title>
                          <v-icon small left>mdi-pencil</v-icon>Edit
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="onDelete(item)"
                        v-show="hasAccess('storage.delete')"
                      >
                        <v-list-item-title>
                          <v-icon small left>delete</v-icon>Delete
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>

                <template v-slot:item.select="{ item }">
                  <span v-if="!item.isFolder">
                    <v-checkbox
                      v-model="form.selectedItems"
                      :value="item.id"
                      v-if="!item.isUsed"
                    />
                    <v-icon v-else>mdi-check</v-icon>
                  </span>
                </template>
              </v-data-table>
            </v-card>
          </template>

          <file-form
            :show="showForm"
            :clientId="clientId"
            :item="selectedItem"
            @onClosed="clearAdd()"
            @onSucceded="refresh()"
          ></file-form>

          <folder-form
            :show="showFormFolder"
            :clientId="clientId"
            :item="selectedItem"
            @onClosed="clearAddFolder()"
            @onSucceded="refresh()"
          ></folder-form>

          <delete-dialog
            :show="showDelete"
            :item="selectedItem"
            @onClosed="clearDelete()"
            @onSucceded="refresh()"
          />

          <delete-folder-dialog
            :show="showDeleteFolder"
            :item="selectedItem"
            @onClosed="clearDelete()"
            @onSucceded="refresh()"
          />
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="closeDelete">Cancel</v-btn>
        <v-btn
          class="info"
          text
          @click="deleteItemConfirm"
          :loading="loadingAction"
          >Attach
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DeleteDialog from "src/views/Pages/Storage/_Delete";
import DeleteFolderDialog from "src/views/Pages/Storage/_DeleteFolder";
import FileForm from "src/views/Pages/Storage/_Form";
import FolderForm from "src/views/Pages/Storage/_FormFolder";
import { mapActions, mapState, mapGetters } from "vuex";
import formatterMixin from "@/mixins/formatterMixin";
import TaskStatusChip from "src/components/TaskStatusChip";
import UsersChip from "components/UsersChip";
import _ from "lodash";
import { getStorage, setStorage } from "src/utils/helpers";
import TagsChip from "components/TagsChip";
import TagsSelect from "components/TagSelect.vue";

const SETTING_LIST_KEY = "storage-columns";
const defaultHeaders = [
  {
    text: "Action",
    value: "actions",
    sortable: false,
    readonly: true,
    active: true,
    width: "5%",
  },
  {
    text: "",
    value: "select",
    sortable: false,
    readonly: true,
    active: true,
    width: "5%",
  },
  { text: "Name", value: "name", sortable: true, active: true },
  { text: "Size", value: "size", sortable: true, active: true },
  { text: "Type", value: "type", sortable: true, active: true },
  { text: "Notes", value: "notes", active: true },
  { text: "Tags", value: "categories", active: true },
  {
    text: "Date Created",
    value: "dateCreated",
    sortable: true,
    active: false,
  },
  {
    text: "Date Modified",
    value: "lastUpdated",
    sortable: true,
    active: false,
  },
];
export default {
  mixins: [formatterMixin],
  props: {
    show: Boolean,
    clientId: Number,
    hideTitle: Boolean,
    item: Object,
    excludedItems: Array,
    object: String,
  },

  components: {
    FileForm,
    DeleteDialog,
    TaskStatusChip,
    UsersChip,
    FolderForm,
    TagsChip,
    DeleteFolderDialog,
    TagsSelect,
  },
  data: () => ({
    dialog: false,
    isEdit: false,
    valid: false,
    namespace: "Storage",
    page: "Files",

    tableOptions: {},

    clients: [],
    users: [],
    statuses: [
      { id: 1, name: "open" },
      { id: 2, name: "closed" },
    ],

    form: {
      selectedItems: [],
    },

    filter: {
      SearchTerm: "",
      clientId: "",
      tags: [],
    },

    menu: false,

    keyword: "",
    headers: defaultHeaders.map((item) => {
      let savedHeaders = getStorage(SETTING_LIST_KEY) || [];
      let nowHeader = savedHeaders.filter(
        (saved) => saved.value === item.value
      );

      if (nowHeader) {
        nowHeader = nowHeader[0];
      }
      return {
        ...item,
        ...nowHeader,
      };
    }),
    tasks: [],
    editedItem: {
      id: null,
      clientId: "",
      userId: "",
      user: "",
      name: "",
      type: "",
      description: "",
      status: "",
      date: "",
    },

    showForm: false,
    showFormFolder: false,
    showDelete: false,
    showDeleteFolder: false,
    showTimeClock: false,
    showTimeEntryForm: false,
    selectedItem: {},
  }),
  computed: {
    ...mapState({
      currentItem: (state) => state.item,
      alert: (state) => state.alert,
    }),

    ...mapGetters("account", ["hasAccess"]),
    ...mapState({
      account: (state) => state.account.data,
      totalItems: (state) => state.storage.metaData.totalEntries,
    }),

    mixedItems() {
      return this.$store.getters["file/getAll"];
    },

    loadingAction() {
      return this.$store.state[this.object].loadingAction;
    },

    loading() {
      return this.$store.state[this.object].loadingTable;
    },

    folderPath() {
      let selected = this.mapFolders.filter((item) => {
        return item.id == this.folderId;
      });
      if (selected.length == 1) {
        //  { ...selected[0], pathName: `Home / ${selected[0].pathName}` };

        let pathIds = selected[0].pathIds;
        let pathNames = selected[0].pathName.split("/");

        let ww = pathIds.map((id, idx) => {
          return [id, pathNames[idx]];
        });
        return [["", "Home"], ...ww];
      } else {
        return [["", "Home"]];
      }
    },

    mapFolders() {
      return this.$store.getters["storageFolder/getAll"];
    },

    folderId() {
      let { folderId } = this.$route.query;
      return folderId;
    },

    isLoading() {
      return this.loading && this.sortedDataItems.length == 0;
    },

    computedFilter() {
      let { clientId, SearchTerm, tags } = this.filter;
      let { sortBy, page, sortDesc, itemsPerPage } = this.tableOptions;
      let params = {
        PageNumber: page ? page : 1,
        PageSize: itemsPerPage > 0 ? itemsPerPage : 1000,
      };

      params.OrderBy = sortBy.length > 0 && sortBy[0] ? sortBy[0] : null;

      if (params.OrderBy) {
        params.Ascending = sortDesc.length > 0 && sortDesc[0] ? false : true;
      } else {
        params.Ascending = false;
      }

      if (clientId) {
        params.clientId = clientId;
      }

      if (SearchTerm) {
        params.SearchTerm = SearchTerm;
      }

      if (this.folderId) {
        params.folderId = this.folderId;
      }

      if (tags && tags.length > 0) {
        params.tags = tags;
      }
      return params;
    },

    activeHeaders() {
      return this.headers.filter((header) => header.active);
    },

    sortedDataItems() {
      let dataItems = [...this.mixedItems];
      let excludedItems = this.excludedItems.map((item) => parseInt(item.id));
      return dataItems.map((item) => {
        let isUsed = excludedItems.includes(parseInt(item.id));
        return {
          ...item,
          isUsed,
        };
      });
    },

    getClientId() {
      const { clientId } = this.$route.params;
      if (clientId) {
        return parseInt(clientId);
      }
      if (this.clientId) {
        return this.clientId;
      }
      return false;
    },

    isAutoAdd() {
      return (
        this.optional(this.$route.query, "action", "") === "add-password-vault"
      );
    },
    tags() {
      return this.filter.tags;
    },
  },
  watch: {
    show(n, o) {
      if (n && !o) {
        this.dialog = true;
      }
    },

    dialog(n) {
      if (!n) {
        this.closeDelete();
      }
    },

    async folderId() {
      await this.refresh();
    },
    tags() {
      this.taskSearch(this.filter);
    },
    headers: {
      handler() {
        setStorage(SETTING_LIST_KEY, this.headers);
      },
      deep: true,
    },
    tableOptions: {
      handler(n, o) {
        if (JSON.stringify(n) !== JSON.stringify(o)) {
          this.refresh();
        }
      },
      deep: true,
    },

    filter: {
      async handler() {
        await this.refresh(true);
      },
      deep: true,
    },
  },
  async mounted() {
    if (this.clientId) {
      this.filter.clientId = this.clientId;
    }

    await this.loadFolders();

    if (this.isAutoAdd) {
      this.onAdd();
    }
  },

  beforeDestroy() {
    if (this.clientId) {
      this.clearTableFilter();
    }
    clearInterval(this.timer);
    this.$store.dispatch("file/clearData");
    this.form.selectedItems = [];
  },

  methods: {
    ...mapActions("file", ["clearTableFilter", "applyTableFilter"]),

    bytesToSize(bytes) {
      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "0 Byte";
      var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },

    ...mapActions({
      taskDelete: "storage/delete",
      taskSearch: "storage/search",
      downloadFile: "storage/download",
      loadFolders: "storageFolder/getAll",
    }),

    refresh: _.debounce(async function () {
      this.refreshTable(true);
    }, 500),

    async refreshTable() {
      this.applyTableFilter({ ...this.computedFilter });
    },

    openFolder(item) {
      this.$router.push(`?folderId=${item.id}`);
      // this.loadAll({folderId:item.id});
    },

    onAdd() {
      this.showForm = true;
      this.selectedItem = {};
    },

    onAddFolder() {
      this.showFormFolder = true;
      this.selectedItem = {};
    },

    onMakeTimeClock(item) {
      this.showTimeClock = true;
      this.selectedItem = item;
    },

    clearMakeTimeClock() {
      this.showTimeClock = false;
      this.selectedItem = {};
    },

    onTimeEntryForm(item) {
      this.showTimeEntryForm = true;
      this.selectedItem = item;
    },

    async clearTimeEntryForm() {
      this.showTimeEntryForm = false;
      this.selectedItem = {};
      await this.refresh();
    },

    onEdit(item) {
      if (!this.hasAccess("storage.edit")) {
        return false;
      }
      if (item.isFolder) {
        this.showFormFolder = true;
      } else {
        this.showForm = true;
      }
      this.selectedItem = item;
    },

    clearAdd() {
      this.showForm = false;
      this.selectedItem = {};
    },

    clearAddFolder() {
      this.showFormFolder = false;
      this.selectedItem = {};
    },

    clearDelete() {
      this.showDelete = false;
      this.showDeleteFolder = false;
      this.selectedItem = {};
    },

    onDelete(item) {
      if (item.isFolder) {
        this.showDeleteFolder = true;
      } else {
        this.showDelete = true;
      }
      this.selectedItem = item;
    },

    async onDownload(item) {
      await this.downloadFile(item);
    },

    async addAttachments(payload) {
      await this.$store.dispatch(`${this.object}/addAttachments`, payload);
    },

    closeDelete() {
      this.dialog = false;
      this.form.selectedItems = [];
      this.$emit("onClosed");
    },

    async deleteItemConfirm() {
      let payload = {
        id: this.item.id,
        attachmentIds: this.form.selectedItems,
      };
      await this.addAttachments(payload);
      if (!this.currentItem.status.error) {
        this.closeDelete();
        this.$emit("onSucceded");
      }
    },
  },
};
</script>